<template>
  <div
    class="play-tabs"
    :class="[
      `play-tabs--${variant}`,
      `play-tabs--align-${align}`,
      { 'play-tabs--vertical': vertical },
    ]"
  >
    <div
      class="play-tabs__nav"
      role="tablist"
      :aria-orientation="vertical ? 'vertical' : 'horizontal'"
    >
      <button
        v-for="(tab, i) in registered"
        :id="tab.tabId"
        :key="tab.tabId"
        type="button"
        role="tab"
        class="play-tabs__tab"
        :class="{ 'is-active': i === activeIndex, 'is-disabled': tab.disabled }"
        :aria-selected="i === activeIndex ? 'true' : 'false'"
        :aria-controls="tab.panelId"
        :tabindex="i === activeIndex ? 0 : -1"
        :disabled="tab.disabled"
        @click="activate(i)"
        @keydown="onKeydown($event, i)"
      >
        <feather-icon
          v-if="tab.icon"
          :icon="tab.icon"
          size="18"
          class="play-tabs__icon"
        />
        <span class="play-tabs__labels">
          <span class="play-tabs__label">{{ tab.title }}</span>
          <span
            v-if="tab.description && i === activeIndex"
            class="play-tabs__desc"
          >{{ tab.description }}</span>
        </span>
      </button>
    </div>

    <div class="play-tabs__content">
      <slot />
    </div>
  </div>
</template>

<script>
/**
 * PlayTabs — tabbed/segmented navigation for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors. Replaces
 * bootstrap-vue's <b-tabs>. Holds <PlayTab> children; the nav is built from each
 * tab's `title` / `icon` / `description` props (read reactively across instances
 * — no vnode teleporting, so locale changes propagate cleanly). `vertical` lays
 * the nav beside the content; `lazy` mounts a panel only once it first activates.
 *
 * Coordination is via provide/inject (NOT $parent), so it is Vue 3-safe: tabs
 * register themselves and ask the group which one is active. Arrow/Home/End keys
 * move between tabs per the WAI-ARIA tabs pattern.
 *
 * VUE3-MIGRATION: `model: { prop: 'value', event: 'input' }` — Vue 3 uses
 * `modelValue` / `update:modelValue`. Change this block + the emit name on
 * migration. provide/inject and the props/slots contract are already Vue 3-safe.
 */
export default {
  name: 'PlayTabs',
  provide() {
    return {
      playTabs: this.api,
    }
  },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    /** Active tab index (v-model target) */
    value: {
      type: Number,
      default: 0,
    },
    /** Lay the nav beside the content instead of above it */
    vertical: {
      type: Boolean,
      default: false,
    },
    /** Nav alignment: start | center | end | fill */
    align: {
      type: String,
      default: 'start',
      validator: v => ['start', 'center', 'end', 'fill'].includes(v),
    },
    /** Active-pill color role */
    variant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'secondary', 'highlight', 'accent'].includes(v),
    },
    /** Mount each panel only once it first becomes active */
    lazy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeIndex: this.value,
      registered: [],
      // Shared contract handed to descendants via inject.
      api: {
        lazy: this.lazy,
        register: this.register,
        unregister: this.unregister,
        isActive: this.isActiveTab,
      },
    }
  },
  watch: {
    value(v) {
      this.activeIndex = v
    },
  },
  methods: {
    register(tab) {
      this.registered.push(tab)
    },
    unregister(tab) {
      const i = this.registered.indexOf(tab)
      if (i === -1) return
      this.registered.splice(i, 1)
      // Keep the active index valid if a tab before/at it was removed.
      if (this.activeIndex >= this.registered.length) {
        this.activeIndex = Math.max(0, this.registered.length - 1)
      }
    },
    isActiveTab(tab) {
      return this.registered.indexOf(tab) === this.activeIndex
    },
    activate(i) {
      const tab = this.registered[i]
      if (!tab || tab.disabled || i === this.activeIndex) return
      this.activeIndex = i
      this.$emit('input', i)
      this.$emit('change', i)
    },
    /** Index of the next non-disabled tab in `dir` (+1/-1), wrapping around */
    nextEnabled(from, dir) {
      const n = this.registered.length
      for (let step = 1; step <= n; step += 1) {
        const i = (from + dir * step + n * step) % n
        if (!this.registered[i].disabled) return i
      }
      return from
    },
    focusTab(i) {
      const tab = this.registered[i]
      const el = tab && tab.$el && this.$el.querySelector(`#${tab.tabId}`)
      if (el) el.focus()
    },
    onKeydown(e, i) {
      const fwd = this.vertical ? 'ArrowDown' : 'ArrowRight'
      const back = this.vertical ? 'ArrowUp' : 'ArrowLeft'
      let target = null

      if (e.key === fwd) target = this.nextEnabled(i, 1)
      else if (e.key === back) target = this.nextEnabled(i, -1)
      else if (e.key === 'Home') target = this.nextEnabled(-1, 1)
      else if (e.key === 'End') target = this.nextEnabled(0, -1)
      else return

      e.preventDefault()
      this.activate(target)
      this.focusTab(target)
    },
  },
}
</script>

<style lang="scss" scoped>
.play-tabs {
  &--vertical {
    display: flex;
    align-items: flex-start;
    gap: 24px;
  }

  /* ── Nav ── */
  &__nav {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  &--vertical &__nav {
    flex-direction: column;
    flex: 0 0 auto;
    min-width: 200px;
  }

  &--align-center &__nav { justify-content: center; }
  &--align-end &__nav { justify-content: flex-end; }
  &--align-fill &__tab { flex: 1 1 0; justify-content: center; }
  &--vertical#{&}--align-fill &__tab { flex: 0 0 auto; }

  /* ── Content ── */
  &__content { margin-top: 18px; }

  &--vertical &__content {
    margin-top: 0;
    flex: 1 1 auto;
    min-width: 0;
  }

  /* ── Tab button ── */
  &__tab {
    --tab-on: var(--c-primary);
    --tab-on-strong: var(--c-primary-strong);
    --tab-on-text: var(--c-primary-on);
    display: inline-flex;
    align-items: center;
    gap: 9px;
    padding: 11px 18px;
    margin: 0;
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 15px;
    letter-spacing: -.01em;
    line-height: 1.2;
    text-align: left;
    color: var(--text-strong);
    background: var(--surface-card);
    border: var(--border-w) solid var(--border-ink);
    border-radius: var(--radius);
    box-shadow: 0 3px 0 var(--border-ink);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition:
      transform var(--dur-fast) var(--ease-bounce),
      box-shadow var(--dur-fast) var(--ease-out),
      background var(--dur-fast) var(--ease-out),
      color var(--dur-fast) var(--ease-out);
  }

  &--secondary &__tab { --tab-on: var(--c-secondary); --tab-on-strong: var(--c-secondary-strong); --tab-on-text: var(--c-secondary-on); }
  &--highlight &__tab { --tab-on: var(--c-highlight); --tab-on-strong: var(--c-highlight-strong); --tab-on-text: var(--c-highlight-on); }
  &--accent    &__tab { --tab-on: var(--c-accent);    --tab-on-strong: var(--c-accent-strong);    --tab-on-text: var(--c-accent-on); }

  &__icon { flex-shrink: 0; }

  &__labels {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__desc {
    font-family: var(--font-body);
    font-weight: 500;
    font-size: 12px;
    line-height: 1.35;
    opacity: .85;
  }

  /* ── States ── */
  &__tab:hover:not(.is-active):not(.is-disabled) {
    transform: translateY(-1px);
    box-shadow: 0 5px 0 var(--border-ink);
  }

  &__tab.is-active {
    background: var(--tab-on);
    color: var(--tab-on-text);
    box-shadow: 0 4px 0 var(--tab-on-strong);
  }

  &__tab:active:not(.is-disabled) {
    transform: translateY(2px);
    box-shadow: var(--shadow-press);
  }

  &__tab:focus-visible {
    outline: 2px solid var(--c-secondary);
    outline-offset: 2px;
  }

  &__tab.is-disabled {
    opacity: .5;
    cursor: not-allowed;
  }
}

@media (prefers-reduced-motion: reduce) {
  .play-tabs__tab { transition: none; }
}
</style>
